import {FormProvider, useForm} from 'react-hook-form';
import Button from 'components/Button';
import DjangoMessages from 'components/DjangoMessages';
import DropdownAction from 'components/DropdownAction';
import InputText from 'components/Form/InputText';
import Icon from 'components/Icon';
import Information from 'components/Information';
import Chain from 'components/Layout/Chain';
import LinkButton from 'components/LinkButton';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import Pill from 'components/Pill';
import SectionHeader from 'components/SectionHeader';
import TablePagination from 'components/Table/TableComponents/TablePagination';
import TableStatic from 'components/Table/TableStatic';
import TooltipWrapper from 'components/Tooltip';
import usePaginatedData from 'hooks/usePaginatedData';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {useFilterEmployees, useSearch} from './hooks';
import {ActiveTeamMembersFormData} from './types';

/**
 * Displays the buttons for things like
 * Inviting new team members, transfering ownership, and distibute licences.
 */
const SearchAndActions = () => {
    const {clearSearch, searchValue} = useSearch();

    const {
        data: {actionButtons, isOwner, isSfConnected},
    } = useBackend<ActiveTeamMembersFormData>();

    return (
        <div className="mt-2 flex justify-between">
            <Chain className="input-wrapper h-10">
                <InputText
                    classNameInput="border-none"
                    name="search"
                    placeholder={t`Search`}
                />
                <Button
                    aria-label={t`Clear search`}
                    className="border-none"
                    disabled={!searchValue}
                    icon="close"
                    kind="secondary"
                    onClick={() => clearSearch()}
                />
            </Chain>
            <Chain id="actions">
                {!isSfConnected && (
                    <TooltipWrapper
                        message={actionButtons.inviteTeamMembers?.failureReason}
                    >
                        <DropdownAction
                            disabled={!actionButtons.inviteTeamMembers?.allowed}
                            kind="secondary"
                            label={t`Invite Team Members`}
                        >
                            <TooltipWrapper
                                message={
                                    actionButtons.inviteOneMember?.failureReason
                                }
                            >
                                <DropdownAction.Link
                                    disabled={
                                        !actionButtons.inviteOneMember?.allowed
                                    }
                                    href={window.Urls.employee_create_view()}
                                >
                                    {t`Invite one Member`}
                                </DropdownAction.Link>
                            </TooltipWrapper>
                            <TooltipWrapper
                                message={
                                    actionButtons.bulkInvite?.failureReason
                                }
                            >
                                <DropdownAction.Link
                                    disabled={
                                        !actionButtons.bulkInvite?.allowed
                                    }
                                    href={window.Urls.partner_import_view()}
                                >
                                    {t`Bulk invite (data import)`}
                                </DropdownAction.Link>
                            </TooltipWrapper>
                        </DropdownAction>
                    </TooltipWrapper>
                )}
                {isOwner && (
                    <TooltipWrapper
                        message={actionButtons.transferOwnership?.failureReason}
                    >
                        <LinkButton
                            disabled={!actionButtons.transferOwnership?.allowed}
                            href={window.Urls.change_owner_react_view()}
                            kind="secondary"
                        >
                            {t`Transfer Ownership`}
                        </LinkButton>
                    </TooltipWrapper>
                )}
                <TooltipWrapper
                    message={actionButtons.distributeLicenses?.failureReason}
                >
                    <LinkButton
                        disabled={!actionButtons.distributeLicenses?.allowed}
                        href={window.Urls.distribute_seats_view()}
                        kind="secondary"
                    >
                        {t`Distribute Licenses`}
                    </LinkButton>
                </TooltipWrapper>
            </Chain>
        </div>
    );
};

/**
 *  This is the first table which lists
 *  the active team members.
 */
const TableTeamMembers = () => {
    const {
        data: {mid},
    } = useBackend<ActiveTeamMembersFormData>();

    const matchedEmployees = useFilterEmployees();

    const {paginatedData, ...pagination} = usePaginatedData(
        matchedEmployees,
        10,
    );

    return (
        <>
            <TableStatic className="mt-2 table-fixed">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column>{t`Name`}</TableStatic.Column>
                        <TableStatic.Column>{t`Licensed`}</TableStatic.Column>
                        <TableStatic.Column colSpan={3} />
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {paginatedData.length === 0 && (
                        <TableStatic.Row>
                            <TableStatic.Cell colSpan={5}>
                                <div className=" flex items-center">
                                    {t`No results matched`}
                                </div>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    )}
                    {paginatedData.map((employee) =>
                        employee == null ? (
                            <TableStatic.Row
                                key={Math.random()}
                                className="bg-grey-100"
                            >
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                                <TableStatic.Cell>&nbsp;</TableStatic.Cell>
                            </TableStatic.Row>
                        ) : (
                            <TableStatic.Row key={employee.mid}>
                                <TableStatic.Cell>
                                    <div className="flex items-center">
                                        <a
                                            href={window.Urls.employee_item_view(
                                                employee.mid,
                                            )}
                                        >
                                            {employee.name}
                                        </a>
                                        {employee.mid === mid && (
                                            <Pill
                                                className="ml-2"
                                                color="grey"
                                                label={t`You`}
                                            />
                                        )}
                                    </div>
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {employee.hasSeat && (
                                        <Icon icon="clients" size="base" />
                                    )}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {employee.state === 'invited' && (
                                        <Pill color="grey" label={t`Invited`} />
                                    )}
                                </TableStatic.Cell>
                                <TableStatic.Cell>
                                    {employee.isOwner && (
                                        <Pill
                                            color="grey"
                                            label={t`Owner/Admin`}
                                        />
                                    )}
                                    {!employee.isOwner && employee.isAdmin && (
                                        <Pill color="grey" label={t`Admin`} />
                                    )}
                                </TableStatic.Cell>
                                <TableStatic.Cell className="float-right">
                                    <TooltipWrapper
                                        message={
                                            employee.permissions.canViewRights
                                                ?.failureReason
                                        }
                                    >
                                        <LinkButton
                                            disabled={
                                                !employee.permissions
                                                    .canViewRights?.allowed
                                            }
                                            href={window.Urls.member_rights_view(
                                                employee.mid,
                                            )}
                                            kind="secondary"
                                            size="xs"
                                        >
                                            {t`View Rights`}
                                        </LinkButton>
                                    </TooltipWrapper>
                                </TableStatic.Cell>
                            </TableStatic.Row>
                        ),
                    )}
                </TableStatic.Body>
            </TableStatic>
            {pagination.pageStop > 1 && (
                <TablePagination className="mt-4" {...pagination} />
            )}
        </>
    );
};

/**
 * This is the second table which lists
 * the API team members.
 */
const TableApiTeamMembers = () => {
    const {data} = useBackend<ActiveTeamMembersFormData>();

    if (data.apiEmployees.length === 0) return null;

    const apiMembersLink = (
        <a key={0} href={window.Urls.makeleaps_api_users()}>
            {t`MakeLeaps API keys`}
        </a>
    );

    return (
        <>
            <SectionHeader
                className="py-2"
                level={2}
                subtitle={jt`Rights for ${apiMembersLink} can be managed like a Team Member.`}
                title={t`API Applications`}
            />
            <TableStatic className="mt-2">
                <TableStatic.Header>
                    <TableStatic.HeaderRow>
                        <TableStatic.Column colSpan={2}>
                            {t`API Application`}
                        </TableStatic.Column>
                    </TableStatic.HeaderRow>
                </TableStatic.Header>
                <TableStatic.Body>
                    {data.apiEmployees.map((employee) => (
                        <TableStatic.Row key={employee.mid}>
                            <TableStatic.Cell>
                                <a
                                    href={window.Urls.employee_item_view(
                                        employee.mid,
                                    )}
                                >
                                    {employee.name}
                                </a>
                            </TableStatic.Cell>
                            <TableStatic.Cell className="float-right">
                                <TooltipWrapper
                                    message={
                                        employee.permissions.canViewRights
                                            ?.failureReason
                                    }
                                >
                                    <LinkButton
                                        disabled={
                                            !employee.permissions.canViewRights
                                                ?.allowed
                                        }
                                        href={window.Urls.member_rights_view(
                                            employee.mid,
                                        )}
                                        kind="secondary"
                                        size="xs"
                                    >
                                        {t`View Rights`}
                                    </LinkButton>
                                </TooltipWrapper>
                            </TableStatic.Cell>
                        </TableStatic.Row>
                    ))}
                </TableStatic.Body>
            </TableStatic>
        </>
    );
};

const LimitUsageNotification = () => {
    const {
        data: {
            activeUserLimit,
            billingStatusIsAtLimit,
            partnerHasStandard,
            planName,
        },
    } = useBackend<ActiveTeamMembersFormData>();

    if (!partnerHasStandard) return null;

    const upgradePlanLink = (
        <a key={0} href={window.Urls.billing_index()}>
            {t`Upgrade your plan`}
        </a>
    );

    if (activeUserLimit) {
        if (billingStatusIsAtLimit) {
            return (
                <Information className="my-4" type="system">
                    {jt`You may have unlimited team members, but you have reached your limit of ${activeUserLimit} full access team members. ${upgradePlanLink} to add additional full-access team members.`}
                </Information>
            );
        }

        return (
            <p>{t`You may have unlimited team members in the ${planName}s plan.`}</p>
        );
    }

    return null;
};

/**
 * Display Email Verification Flagged Banner.
 * TODO: 2024-03-29 - Check if this flag is still required
 */
const DisplayEmailVerificationFlaggedBanner = () => {
    const {checkFlag, data} = useBackend<ActiveTeamMembersFormData>();

    if (!checkFlag('enforce_email_verification')) return null;

    if (data.isAdmin && !data.primaryEmailVerified) {
        return (
            <Information icon="danger" type="danger">
                {t`For better security and service, MakeLeaps requires you to complete Primary Email verification before inviting team members.`}
                <div className="mt-4 flex justify-end gap-4">
                    <LinkButton
                        href={window.Urls.email_settings_view()}
                        kind="secondary"
                        size="xs"
                    >
                        {t`To Email Settings`}
                    </LinkButton>
                </div>
            </Information>
        );
    }

    return null;
};

const ActiveTeamMembers = () => {
    const {
        data: {navigationTabs},
    } = useBackend<ActiveTeamMembersFormData>();

    const methods = useForm();

    return (
        <div>
            <DjangoMessages />
            <FormProvider {...methods}>
                <NavigationTabs.Team
                    className="my-4"
                    selection="members"
                    {...navigationTabs}
                />
                <DisplayEmailVerificationFlaggedBanner />
                <SectionHeader title={t`Active Team Members`} />
                <LimitUsageNotification />
                <SearchAndActions />
                <TableTeamMembers />
                <TableApiTeamMembers />
            </FormProvider>
        </div>
    );
};

export default ActiveTeamMembers;
